import { getErrorMessage } from "@/util/helpers";
import Trainplanet from "@/util/trainplanet.api";

export default {
  data: () => ({
    taxiMessageListener: null,
    iframeHeight: 300,
    cacheBust: Date.now(),
  }),
  computed: {
    taxiWidgetBaseUrl() {
      let targetUrl = "https://customer.trainplanet.com";
      const hostnameParts = window.location.hostname.split(".");

      if (hostnameParts.includes("dev") || window.location.hostname === "localhost") {
        targetUrl = "https://dev.customer.trainplanet.com";
      }
      return targetUrl;
    },
  },
  methods: {
    postMessageToIframe(message) {
      const iframe = this.$refs.taxiFrame;
      if (!iframe) return;

      try {
        const serializedMessage = JSON.stringify(message);
        iframe.contentWindow.postMessage(serializedMessage, this.taxiWidgetBaseUrl);
      } catch (error) {
        this.$store.dispatch("error", error);
      }
    },
    async initializeTaxiWidget() {
      const iframe = this.$refs.taxiFrame;
      if (!iframe) return;

      if (!this.taxiMessageListener) {
        this.taxiMessageListener = (event) => {
          const { type, data } = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
          if (type === "setHeight") {
            if (this.blockHeightChange) return;
            this.iframeHeight = data;
          } else if (type === "addTaxi") {
            this.addTaxi(data);
          } else if (type === "listTaxis") {
            this.listTaxis(data);
          } else if (type === "createAddress") {
            this.createAddress(data);
          } else if (type === "scrollTo") {
            const { position } = data;
            const frameRect = iframe.getBoundingClientRect();

            if (position === "middle") {
              if (this.inDialog) {
                const dialog = this.$el.closest(".v-dialog--active");
                if (dialog) {
                  const scrollableMiddle = (dialog.scrollHeight - dialog.clientHeight) / 2;
                  dialog.scrollTo({
                    top: scrollableMiddle,
                    behavior: "smooth",
                  });
                  return;
                }
              }

              const middlePosition = frameRect.top + window.scrollY + frameRect.height / 2 - window.innerHeight / 2;
              window.scrollTo({
                top: middlePosition,
                behavior: "smooth",
              });
            }
          } else if (type === "notify") {
            const { message, type } = data;
            if (type === "success") {
              this.$store.dispatch("success", message);
            } else {
              this.$store.dispatch("error", message);
            }
          } else if (type === "passengerAction") {
            this.handlePassengerAction(data);
          }
        };
        window.addEventListener("message", this.taxiMessageListener);
      }

      this.postMessageToIframe({ type: "setLoading", data: true });
      try {
        this.postMessageToIframe({ type: "orderId", data: this.orderId });
        this.postMessageToIframe({ type: "orderToken", data: this.orderToken });

        this.postMessageToIframe({ type: "theme", data: "mango" });
        this.postMessageToIframe({ type: "locale", data: "en-SE" });
        this.postMessageToIframe({
          type: "orderHasAccount",
          data: !!this.orderAccountId,
        });

        this.postMessageToIframe({ type: "showBasket", data: false });

        const taxiPayload = await Trainplanet.getTaxiPayload(this.orderId, this.selectedTenant);
        this.postMessageToIframe({ type: "taxiPayload", data: taxiPayload });
      } catch (error) {
        this.$store.dispatch("error", error);
      } finally {
        this.postMessageToIframe({ type: "setLoading", data: false });
      }
    },
    async addTaxi(data) {
      const deserializedPayload = JSON.parse(data);

      if (this.inDialog) {
        this.handleFrameLoading({ visibility: true, white: this.whiteWidgetLoading });
      }
      this.$store.dispatch("loading", true);
      try {
        await Trainplanet.addItems(this.orderId, deserializedPayload);

        await this.$store.dispatch("success", "Taxi has been added successfully!");
      } catch (error) {
        await this.$store.dispatch("error", getErrorMessage(error));
      } finally {
        if (this.inDialog) {
          this.handleFrameLoading({ visibility: false, white: this.whiteWidgetLoading });
        }
        this.$store.dispatch("loading", false);
        this.updateOrder();
        if (this.inDialog) {
          this.$emit("closeDialog");
        }
      }
    },
    async createAddress(data) {
      const { type, payload } = JSON.parse(data);

      if (this.inDialog) {
        this.handleFrameLoading({ visibility: true, white: this.whiteWidgetLoading });
      }
      this.$store.dispatch("loading", true);
      try {
        const { data: responseData } = await this.AccountModel.createAddress(payload, this.orderAccountId);
        this.postMessageToIframe({ type: "addressCreated", data: { type, payload: responseData } });
        this.$store.dispatch("success", "Address has been saved successfully!");
      } catch (error) {
        await this.$store.dispatch("error", getErrorMessage(error));
      } finally {
        if (this.inDialog) {
          this.handleFrameLoading({ visibility: false, white: this.whiteWidgetLoading });
        }
        this.$store.dispatch("loading", false);
      }
    },
    async handlePassengerAction(data) {
      const { passengerId, passengerAction, payload } = data;
      const accountId = this.orderAccountId;

      if (this.inDialog) {
        this.handleFrameLoading({ visibility: true, white: this.whiteWidgetLoading });
      }
      this.$store.dispatch("loading", true);
      try {
        if (passengerAction === "save") {
          const { data } = await this.AccountModel.registerPassenger(accountId, payload);
          this.postMessageToIframe({ type: "passengerSaved", data });
        } else if (passengerAction === "update") {
          const { data } = await this.AccountModel.updatePassenger(passengerId, payload, accountId);
          this.postMessageToIframe({ type: "passengerUpdated", data });
        }
        if (passengerAction === "save") {
          await this.$store.dispatch("success", "Passenger has been saved successfully!");
        } else {
          await this.$store.dispatch("success", "Passenger has been updated successfully!");
        }
      } catch (error) {
        await this.$store.dispatch("error", getErrorMessage(error));
      } finally {
        if (this.inDialog) {
          this.handleFrameLoading({ visibility: false, white: this.whiteWidgetLoading });
        }
        this.$store.dispatch("loading", false);
      }
    },
    async listTaxis(data) {
      const deserializedPayload = JSON.parse(data);
      deserializedPayload.orderId = this.orderId;

      if (this.inDialog) {
        this.handleFrameLoading({ visibility: true, white: this.whiteWidgetLoading });
      }
      this.$store.dispatch("loading", true);

      try {
        const res = await Trainplanet.getTaxiOptions(deserializedPayload);
        this.postMessageToIframe({ type: "listTaxis", data: res });
      } catch (error) {
        await this.$store.dispatch("error", getErrorMessage(error));
      } finally {
        if (this.inDialog) {
          this.handleFrameLoading({ visibility: false, white: this.whiteWidgetLoading });
        }
        this.$store.dispatch("loading", false);
      }
    },
    handleFrameLoading(action) {
      this.postMessageToIframe({ type: "setLoading", data: action });
    },
  },
  beforeDestroy() {
    if (this.taxiMessageListener) {
      window.removeEventListener("message", this.taxiMessageListener);
      this.taxiMessageListener = null;
    }
  },
};
